/**
 * Outlet Card Component
 * Displays a single outlet with stats and actions
 */

import { OutletWithStats } from '@/renderer/types/outlet';
import {
  Building2,
  Edit2,
  MapPin,
  Trash2,
  Users,
  UtensilsCrossed,
} from 'lucide-react';
import React from 'react';
import { ONBOARDING_TOUR_TARGETS } from '../../services/onboardingTourService';

interface OutletCardProps {
  canManage?: boolean;
  isTourPrimary?: boolean;
  outlet: OutletWithStats;
  isCurrentOutlet: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export const OutletCard: React.FC<OutletCardProps> = ({
  canManage = true,
  isTourPrimary = false,
  outlet,
  isCurrentOutlet,
  onEdit,
  onDelete,
}) => {
  return (
    <div
      data-onboarding-tour={
        isTourPrimary ? ONBOARDING_TOUR_TARGETS.primaryOutletCard : undefined
      }
      className={`relative bg-white rounded-xl border-2 p-5 transition-all ${
        isCurrentOutlet
          ? 'border-emerald-500 shadow-emerald-100 shadow-lg'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      {isCurrentOutlet && (
        <span className="absolute -top-3 left-4 px-3 py-1 text-xs font-semibold bg-emerald-500 text-white rounded-full">
          Current
        </span>
      )}

      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
          <Building2 size={24} className="text-gray-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {outlet.name}
          </h3>
          {outlet.address && (
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
              <MapPin size={14} />
              <span className="truncate">{outlet.address}</span>
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users size={16} className="text-blue-500" />
          <span>{outlet.user_count || 0} Users</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <UtensilsCrossed size={16} className="text-purple-500" />
          <span>{outlet.menu_item_count || 0} Items</span>
        </div>
      </div>

      {canManage ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onEdit}
            data-onboarding-tour={
              isTourPrimary
                ? ONBOARDING_TOUR_TARGETS.primaryOutletEditButton
                : undefined
            }
            className="flex-1 py-2 px-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
          >
            <Edit2 size={14} />
            Edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={isCurrentOutlet}
            className="py-2 px-3 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              isCurrentOutlet ? 'Cannot delete current outlet' : 'Delete outlet'
            }
          >
            <Trash2 size={14} />
          </button>
        </div>
      ) : null}
    </div>
  );
};
